/**
 * @fileoverview Provides a TypeScript language server implementation for retrieving code context, including definitions, errors, symbols, and references. The module wraps the language server protocol to enable detailed code analysis and supports operations like tracking file changes and querying code information for specific line ranges.
 */

import { spawn } from 'child_process'
import { readFileSync } from 'fs'
import { createMessageConnection, MessageConnection } from 'vscode-jsonrpc'
import { StreamMessageReader, StreamMessageWriter } from 'vscode-jsonrpc/node'
import { getDeclarationsFromFile } from './ast'
import { DefaultMap } from './default_map'
import { error } from '@iterator/shared-node/util/logging'

export type CodeError = {
  message: string
  start: { row: number; col: number }
  end: { row: number; col: number }
}

type DiagnosticError = {
  range: {
    start: { line: number; character: number }
    end: { line: number; character: number }
  }
  message: string
  severity: number
  code: number
}

export type Symbol = {
  kind: number
  name: string
  containerName: string
  location: {
    uri: string
    range: {
      start: { line: number; character: number }
      end: { line: number; character: number }
    }
  }
}

export type SymbolReference = {
  uri: string
  range: {
    start: { line: number; character: number }
    end: { line: number; character: number }
  }
}

/**
 * A wrapper around language server APIs to provide LLM context.
 *
 * The language server will be initialized in watch mode and passed to Autocomplete or
 * Multicomplete implementations. Watch mode will keep the language server up to date
 * so we don't need to re-load every file on every completion request.
 */
export interface LanguageServer {
  /**
   * For the given file, find referenced variable, function, class, etc in the given range.
   */
  getDefinitionsInRange(filePath: string, startLine: number, endLine: number): Promise<string[]>

  /**
   * For the given file, find errors in the given range.
   */
  getErrorsInRange(filePath: string, startLine: number, endLine: number): Promise<CodeError[]>

  /**
   * For the given file, get all symbols in the file.
   */
  getSymbolsAndReferences(
    filePath: string,
    startLine: number,
    endLine: number,
  ): Promise<{ symbol: Symbol; references: SymbolReference[] }[]>

  /**
   * Hook to keep the context provider up to date as the file changes.
   */
  onFileChange(filePath: string, fileContent: string): void
}

export class TypescriptLanguageServer implements LanguageServer {
  private connection: MessageConnection
  private ready = false
  private version = 0

  private fileToErrors: Map<string, CodeError[]> = new DefaultMap(() => [])

  constructor(private directory: string) {
    const tsServer = spawn('typescript-language-server', ['--stdio'])

    this.connection = createMessageConnection(
      new StreamMessageReader(tsServer.stdout),
      new StreamMessageWriter(tsServer.stdin),
    )
  }

  async start(): Promise<void> {
    this.connection.listen()

    return this.connection
      .sendRequest('initialize', {
        processId: process.pid,
        rootUri: `file://${this.directory}`,
        capabilities: {
          workspace: {
            configuration: true,
          },
          textDocument: {
            publishDiagnostics: {
              relatedInformation: true,
            },
          },
        },
        workspaceFolders: [
          {
            uri: `file://${this.directory}`,
            name: 'workspace',
          },
        ],
      })
      .then(() => {
        this.connection.onNotification(
          'textDocument/publishDiagnostics',
          ({ uri, diagnostics }: { uri: string; diagnostics: DiagnosticError[] }) => {
            const codeErrors = diagnostics.map((d) => ({
              message: d.message,
              start: { row: d.range.start.line, col: d.range.start.character },
              end: { row: d.range.end.line, col: d.range.end.character },
            }))
            const sortedErrors = codeErrors.sort((a, b) => {
              if (a.start.row !== b.start.row) return a.start.row - b.start.row
              return a.start.col - b.start.col
            })
            const combinedErrors = sortedErrors.reduce((acc, error) => {
              if (acc.length === 0 || acc[acc.length - 1]?.message !== error.message) {
                acc.push(error)
              } else if (acc[acc.length - 1]?.start.row === error.start.row) {
                acc[acc.length - 1]!.end = error.end
              }
              return acc
            }, [] as CodeError[])

            this.fileToErrors.set(uri, combinedErrors)
          },
        )
        this.connection.sendNotification('initialized', {})
        this.connection.sendNotification('workspace/didChangeConfiguration', {
          settings: {
            typescript: {
              tsdk: require.resolve('typescript').replace(/typescript\.js$/, ''),
            },
          },
        })

        this.ready = true
      })
  }

  onFileRemove(filePath: string): void {
    this.connection.sendNotification('textDocument/didClose', {
      textDocument: { uri: `file://${filePath}` },
    })
  }

  onFileAdd(filePath: string, fileContent: string): void {
    if (!this.ready) {
      error(`Language server received file change before initialization path=${filePath}`)
      return
    }

    this.connection.sendNotification('textDocument/didOpen', {
      textDocument: {
        uri: `file://${filePath}`,
        languageId: 'typescript',
        version: this.version++,
        text: fileContent,
      },
    })
  }

  onFileChange(filePath: string, fileContent: string): void {
    this.connection.sendNotification('textDocument/didChange', {
      textDocument: {
        uri: `file://${filePath}`,
        version: this.version++,
      },
      contentChanges: [{ text: fileContent }],
    })
  }

  async getDefinitionsInRange(
    filePath: string,
    startLine: number,
    endLine: number,
  ): Promise<string[]> {
    return await findAllTypesOnLines(this.connection, filePath, startLine, endLine)
  }

  async getErrorsInRange(
    filePath: string,
    startLine: number,
    endLine: number,
  ): Promise<CodeError[]> {
    const allErrors = this.fileToErrors.get(`file://${filePath}`) || []
    return allErrors.filter((e) => !(e.end.row < startLine || e.start.row > endLine))
  }

  async getSymbolsAndReferences(
    filePath: string,
    startLine: number,
    endLine: number,
  ): Promise<{ symbol: Symbol; references: SymbolReference[] }[]> {
    return await findAllSymbolsInFile(this.connection, filePath, startLine, endLine)
  }
}

export async function findAllTypesOnLines(
  connection: MessageConnection,
  filePath: string,
  startLine: number,
  endLine: number,
): Promise<string[]> {
  const fileUri = `file://${filePath}`

  const uniqueTypes = new Set<string>()

  const fileContent = readFileSync(filePath, 'utf8')
  const lines = fileContent.split('\n')

  const pathToPosition = new Map<string, { row: number; column: number }[]>()

  for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
    const lineLength = lines[lineNumber]?.length || 0

    try {
      for (let character = 0; character < lineLength; character++) {
        const position = { line: lineNumber, character }
        const possibleTypeDef = await getTypeDefinitionsAtPosition(connection, fileUri, position)
        if (possibleTypeDef) {
          pathToPosition.set(possibleTypeDef.path, [
            ...(pathToPosition.get(possibleTypeDef.path) || []),
            possibleTypeDef,
          ])
        }
      }
    } catch (error) {
      console.error('Error finding type definitions:', error)
    }
  }

  for (const [path, positions] of Array.from(pathToPosition.entries())) {
    const declarations = await getDeclarationsFromFile(path, positions)
    declarations.forEach((d) => uniqueTypes.add(d))
  }

  return Array.from(uniqueTypes)
}

async function getTypeDefinitionsAtPosition(
  connection: MessageConnection,
  fileUri: string,
  position: { line: number; character: number },
): Promise<{ path: string; row: number; column: number } | undefined> {
  const typeDefResult = await connection.sendRequest('textDocument/typeDefinition', {
    textDocument: { uri: fileUri },
    position,
  })

  if (Array.isArray(typeDefResult) && typeDefResult.length > 0) {
    const typeDef = typeDefResult[0]
    const url = decodeURIComponent(typeDef.uri)

    if (!url.includes('node_modules')) {
      return {
        path: url.replace('file://', ''),
        row: typeDef.range.start.line,
        column: typeDef.range.start.character,
      }
    }
  }
  return undefined
}

async function findAllSymbolsInFile(
  connection: MessageConnection,
  filePath: string,
  startLine: number,
  endLine: number,
): Promise<{ symbol: Symbol; references: SymbolReference[] }[]> {
  const fileUri = 'file://' + filePath
  const symbols: Symbol[] = await connection.sendRequest('textDocument/documentSymbol', {
    textDocument: { uri: fileUri },
  })
  const symbolsInRange = symbols.filter(
    (s) => s.location.range.start.line >= startLine && s.location.range.start.line <= endLine,
  )
  return await Promise.all(
    symbolsInRange.map(async (s) => ({
      symbol: s,
      references: await getSymbolReferences(connection, s),
    })),
  )
}

async function getSymbolReferences(
  connection: MessageConnection,
  symbol: Symbol,
): Promise<SymbolReference[]> {
  return await connection.sendRequest('textDocument/references', {
    textDocument: { uri: symbol.location.uri },
    position: symbol.location.range.start,
    context: { includeDeclaration: true },
  })
}
